import { footprint, getItemDef, type Inventory } from "@rietz/shared";
import { useRef, useState, type PointerEvent, type ReactElement, type ReactNode } from "react";
import type { OpenContainer } from "../store.js";
import {
  LINE,
  LINE_STRONG,
  RADIUS,
  SHADOW_CARD,
  SHADOW_MODAL,
  SURFACE,
  TEXT_DIM,
  UI_FAMILY,
} from "../theme.js";
import { CELL, instanceAt, viewportToUi, type DragState } from "./drag.js";
import { TileBody } from "./tile.js";

/**
 * The chrome an inventory screen is made of: a framed panel, and the item
 * hanging off the cursor. Shared between the raid's inventory and the menu's
 * stash; neither knows where it is used.
 */

export interface PanelProps {
  title: string;
  /**
   * Float over the inventory layout instead of taking a flex column — opened
   * bags and boxes, which are dialogs.
   */
  overlay?: boolean;
  /**
   * Where an overlay panel first appears, as a diagonal offset from centre. THE
   * SPAWN POINT ONLY: read once as the initial drag offset so a stack lands
   * staggered; a later `nudge` must not yank a window the player has moved.
   */
  nudge?: number;
  /**
   * Called on any press inside an overlay panel so the window can be raised.
   * CAPTURE PHASE: tile presses are stopped by the drag layer, so a bubbling
   * handler would only see empty chrome. The raise is a store re-order — every
   * panel shares one z-index, so stacking IS DOM order, and stable keys keep
   * React moving nodes rather than remounting.
   */
  onRaise?: () => void;
  /** Key hint shown next to the close button, e.g. "Tab". */
  hint?: string;
  /**
   * Whatever this panel puts in its header: a weight, a fill counter, a button.
   * A slot rather than a second header component.
   */
  header?: ReactNode;
  /**
   * Omitted for a panel that cannot be dismissed — the menu's gear panels are
   * the screen.
   */
  onClose?: () => void;
  children: ReactNode;
}

/** Where a dialog has been dragged to, relative to its centred resting place. */
interface PanelOffset {
  dx: number;
  dy: number;
}

interface PanelMove {
  pointerId: number;
  startX: number;
  startY: number;
  offset: PanelOffset;
}

export function Panel(props: PanelProps): ReactElement {
  const overlay = props.overlay === true;
  const [offset, setOffset] = useState<PanelOffset>({
    dx: props.nudge ?? 0,
    dy: props.nudge ?? 0,
  });
  const move = useRef<PanelMove | null>(null);

  /**
   * A dialog is moved by its title bar, as the Werkbank's window is. Kept to
   * the header, and `stopPropagation` so the root's capture logic never sees
   * it.
   */
  const onHeaderDown = (ev: PointerEvent<HTMLElement>): void => {
    if (!overlay || ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();
    ev.currentTarget.setPointerCapture(ev.pointerId);
    move.current = { pointerId: ev.pointerId, startX: ev.clientX, startY: ev.clientY, offset };
  };
  const onHeaderMove = (ev: PointerEvent<HTMLElement>): void => {
    const m = move.current;
    if (m === null || m.pointerId !== ev.pointerId) return;
    ev.stopPropagation();
    // Pointer deltas are viewport pixels; the UI root is scaled, so the same
    // delta has to be un-scaled before it is applied as a CSS translation.
    const root = document.getElementById("ui");
    const rect = root?.getBoundingClientRect();
    const scaleX = root !== null && rect !== undefined && root.clientWidth > 0 ? rect.width / root.clientWidth : 1;
    const scaleY = root !== null && rect !== undefined && root.clientHeight > 0 ? rect.height / root.clientHeight : 1;
    setOffset({
      dx: m.offset.dx + (ev.clientX - m.startX) / scaleX,
      dy: m.offset.dy + (ev.clientY - m.startY) / scaleY,
    });
  };
  const onHeaderUp = (ev: PointerEvent<HTMLElement>): void => {
    if (move.current?.pointerId !== ev.pointerId) return;
    ev.stopPropagation();
    move.current = null;
  };

  return (
    <section
      role={overlay ? "dialog" : undefined}
      aria-label={overlay ? props.title : undefined}
      onPointerDownCapture={overlay && props.onRaise !== undefined ? props.onRaise : undefined}
      style={{
        position: overlay ? "absolute" : undefined,
        left: overlay ? "50%" : undefined,
        top: overlay ? "50%" : undefined,
        zIndex: overlay ? 20 : undefined,
        transform: overlay
          ? `translate(-50%, -50%) translate(${offset.dx}px, ${offset.dy}px)`
          : undefined,
        padding: 16,
        // A dialog is a shade heavier than a panel on the page: rounder, opaque
        // (it must occlude the grid under it), a stronger edge and more lift.
        borderRadius: overlay ? 12 : RADIUS,
        background: overlay ? "rgb(19, 22, 26)" : SURFACE,
        border: `1px solid ${overlay ? LINE_STRONG : LINE}`,
        boxShadow: overlay ? SHADOW_MODAL : SHADOW_CARD,
        boxSizing: "border-box",
        maxWidth: props.overlay === true ? "calc(100% - 32px)" : "100%",
        // Tall loadouts scroll rather than run off the bottom of a small window.
        // The cell maths survives it: every rect is read live from the DOM.
        maxHeight: props.overlay === true ? "calc(100% - 32px)" : "100%",
        overflowY: "auto",
        // A classic scrollbar would otherwise take its width out of the
        // fixed-width
        // grid and clip the rightmost column; reserve the gutter up front.
        scrollbarGutter: "stable",
        // `overflow-y: auto` would make the other axis scrollable too; grids
        // have
        // fixed cell geometry.
        overflowX: "hidden",
      }}
    >
      <header
        onPointerDown={onHeaderDown}
        onPointerMove={onHeaderMove}
        onPointerUp={onHeaderUp}
        onPointerCancel={onHeaderUp}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 12,
          cursor: overlay ? "move" : undefined,
          touchAction: overlay ? "none" : undefined,
        }}
      >
        <span style={{ font: `600 14px/1.2 ${UI_FAMILY}`, letterSpacing: 0 }}>{props.title}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {props.header}
          {props.hint === undefined ? null : (
            <span style={{ fontSize: 11, color: TEXT_DIM }}>{props.hint}</span>
          )}
          {props.onClose === undefined ? null : (
            <button
              type="button"
              className="invclose"
              onClick={props.onClose}
              // A press on × is a close, not the start of a move.
              onPointerDown={(ev) => {
                ev.stopPropagation();
              }}
              title="Close"
            >
              ×
            </button>
          )}
        </span>
      </header>
      {props.children}
    </section>
  );
}

/**
 * The item under the cursor. `pointer-events: none` is load-bearing: `zoneAt`
 * uses `elementFromPoint`, and a ghost that took the mouse would be all it ever
 * found.
 */
export function Ghost(props: {
  drag: DragState | null;
  inv: Inventory | null;
  container: OpenContainer | null;
}): ReactElement | null {
  const { drag, inv, container } = props;
  if (drag === null || inv === null) return null;

  const def = getItemDef(drag.defId);
  if (def === null) return null;
  const item = instanceAt(inv, container, drag.from, drag.iid);
  if (item === null) return null;
  const f = footprint(def, drag.rot);
  const pointer = viewportToUi(drag.px, drag.py);

  return (
    <div
      style={{
        position: "fixed",
        left: pointer.x - drag.grabX,
        top: pointer.y - drag.grabY,
        width: f.w * CELL,
        height: f.h * CELL,
        pointerEvents: "none",
        opacity: 0.88,
        filter: "drop-shadow(0 4px 10px rgba(0, 0, 0, 0.6))",
        zIndex: 40,
      }}
    >
      <TileBody def={def} item={item} rot={drag.rot} />
    </div>
  );
}
